"use client";
import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductGrid from './ProductGrid';
import ProductCarousel from './ProductCarousel';
import Cat from './Cat';
import CategoryFooter from './CategoryFooter';
import { ArrowRight, Eye, MoveRight } from 'lucide-react';

import PromotionalSection from './PromotionalSection';
import { ProductContext } from '../context/ProductContext';
import { useCategories } from '../context/CategoriesContext';

function Products() {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchProductsByCategory } = useContext(ProductContext);
  const { categories, loading: categoriesLoading } = useCategories();

  const [categoryProductsMap, setCategoryProductsMap] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});
  const [fetchedCategories, setFetchedCategories] = useState(new Set());

  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';

  // Improved fetch products for each category - no race conditions
  const fetchCategoryProducts = useCallback(async (categoryId) => {
    // Prevent duplicate fetches
    if (loadingCategories[categoryId] || fetchedCategories.has(categoryId)) {
      return;
    }

    console.log(`Fetching products for category: ${categoryId}`);
    setLoadingCategories(prev => ({ ...prev, [categoryId]: true }));

    try {
      const products = await fetchProductsByCategory(categoryId);

      // Update the map with the returned products
      setCategoryProductsMap(prev => ({
        ...prev,
        [categoryId]: products || []
      }));

      // Mark as fetched
      setFetchedCategories(prev => new Set([...prev, categoryId]));

      console.log(`Successfully fetched ${products?.length || 0} products for category ${categoryId}`);
    } catch (error) {
      console.error(`Error fetching products for category ${categoryId}:`, error);
      // Set empty array on error to prevent infinite retries
      setCategoryProductsMap(prev => ({
        ...prev,
        [categoryId]: []
      }));
    } finally {
      setLoadingCategories(prev => ({ ...prev, [categoryId]: false }));
    }
  }, [fetchProductsByCategory, loadingCategories, fetchedCategories]);

  // Fetch products for all categories on mount
  useEffect(() => {
    if (categories && categories.length > 0) {
      console.log(`Starting to fetch products for ${categories.length} categories`);
      categories.forEach(category => {
        fetchCategoryProducts(category.id);
      });
    }
  }, [categories, fetchCategoryProducts]);

  // Apply search filtering to category products
  const getFilteredCategoryProducts = useCallback((products) => {
    if (!searchQuery || !products) return products;

    return products.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  // Calculate middle position for ProductCarousel
  const middleIndex = useMemo(() => {
    return Math.ceil(categories.length / 2);
  }, [categories.length]);

  // Helper function to scroll with header offset
  const scrollToElementWithOffset = (element, offset = 100) => {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  // Handle View All button click
  const handleViewAllClick = (categoryId) => {
    navigate(`/products/category/${categoryId}`);
  };

  // Scroll to section if hash is present in URL, or scroll to top on mount
  useEffect(() => {
    if (location.hash === '#browse-home-decoration') {
      const element = document.getElementById('browse-home-decoration');
      if (element) {
        // Scroll with header offset so title appears just below header
        scrollToElementWithOffset(element, 100);

        // Remove hash from URL after scroll is complete
        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
        }, 1000);
      }
    } else if (location.hash.startsWith('#category-')) {
      // Handle category-specific scrolling
      const categoryId = location.hash.replace('#category-', '');
      const element = document.getElementById(`category-${categoryId}`);
      if (element) {
        // Scroll with header offset so title appears just below header
        scrollToElementWithOffset(element, 100);

        // Remove hash from URL after scroll is complete
        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
        }, 1000);
      } else {
        // If element not found immediately, try again after a short delay
        // This handles cases where the category section hasn't rendered yet
        setTimeout(() => {
          const delayedElement = document.getElementById(`category-${categoryId}`);
          if (delayedElement) {
            // Scroll with header offset so title appears just below header
            scrollToElementWithOffset(delayedElement, 100);
            window.history.replaceState(null, '', window.location.pathname);
          }
        }, 500);
      }
    } else {
      // Scroll to top when component mounts
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.hash]);

  // Category Separator Component
  const CategorySeparator = () => (
    <div className="w-full my-3 flex justify-center" aria-hidden="true">
      <div className="w-full max-w-[1308px] px-4">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      </div>
    </div>
  );

  if (categoriesLoading) {
    return (
      <div className="flex overflow-hidden flex-col bg-orange-50 bg-opacity-90">
        <Cat/>
        <main className="flex flex-col items-center w-full">
          <div className="mt-10 text-center text-gray-600" role="status" aria-live="polite">
            <div className="inline-flex items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600" aria-hidden="true"></div>
              Loading categories...
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden flex-col bg-orange-50 bg-opacity-90">
      <Cat/>

      <main className="flex flex-col items-center w-full">
        <h1
          id="browse-home-decoration"
          className="mt-5 text-6xl font-semibold text-stone-900 max-md:text-4xl text-center"
          style={{ fontFamily: 'Gloock, serif' }}
        >
          Browse Products
        </h1>

        <div className="w-full">
          {(() => {
            let visibleCategoryIndex = 0;
            const visibleCategories = categories.filter(category => {
              const categoryProductsList = categoryProductsMap[category.id] || [];
              const filteredProducts = getFilteredCategoryProducts(categoryProductsList);
              const isLoading = loadingCategories[category.id];
              return isLoading || filteredProducts.length > 0;
            });
            const visibleMiddleIndex = Math.ceil(visibleCategories.length / 2);

            return categories.map((category, index) => {
              const categoryProductsList = categoryProductsMap[category.id] || [];
              const filteredProducts = getFilteredCategoryProducts(categoryProductsList);
              const isLoading = loadingCategories[category.id];

              // Only render category section if it has products or is loading
              if (!(isLoading || filteredProducts.length > 0)) {
                return null;
              }

              visibleCategoryIndex++;

              return (
                <React.Fragment key={category.id}>
                  <section className="mt-3 w-full" aria-labelledby={`category-${category.id}`}>
                    {/* Category Title */}
                    <div className="mb-8 text-center relative">
                      {/* Desktop View All Button - Top Right */}
                      <div className="hidden lg:block absolute top-0 right-4 xl:right-8">
                        <button
                          onClick={() => handleViewAllClick(category.id)}
                          className="flex items-center gap-2 px-4 py-1 bg-stone-900 text-white rounded-xl hover:bg-stone-800 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-xl group"
                          aria-label={`View all products in ${category.title}`}
                        >
                          {/* <Eye size={16} /> */}
                          <span className="font-normal">View All</span>
                          <MoveRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                        </button>
                      </div>

                      <h2
                        id={`category-${category.id}`}
                        className="text-3xl font-semibold text-stone-900 max-md:text-3xl mb-2"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        {category.title}
                      </h2>
                      {category.subtitle && (
                        <p className="text-md max-w-4xl px-3 md:px-0 mx-auto text-gray-600 max-md:text-base">
                          {category.subtitle}
                        </p>
                      )}

                      {/* Mobile/Tablet View All Button - Below Title and Description */}
                      <div className="lg:hidden mt-4">
                        <button
                          onClick={() => handleViewAllClick(category.id)}
                          className="inline-flex items-center text-sm gap-2 px-6 py-2 bg-stone-900 text-white rounded-xl hover:bg-stone-800 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg group"
                          aria-label={`View all products in ${category.title}`}
                        >
                          {/* <Eye size={18} /> */}
                          <span className="font-normal">View All {category.title}</span>
                          <MoveRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                        </button>
                      </div>
                    </div>

                    {/* Category Products */}
                    {isLoading ? (
                      <div className="mt-10 text-center text-gray-600" role="status" aria-live="polite">
                        <div className="inline-flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" aria-hidden="true"></div>
                          Loading {category.title} products...
                        </div>
                      </div>
                    ) : (
                      <ProductGrid filteredProducts={filteredProducts} />
                    )}
                  </section>

                  {/* Add separator line after each category */}
                  <CategorySeparator />

                  {/* Insert ProductCarousel in the middle of visible categories */}
                  {visibleCategoryIndex === visibleMiddleIndex && (
                    <div className="w-full my-6">
                      <ProductCarousel />
                      {/* Add separator after carousel too */}
                      <CategorySeparator />
                    </div>
                  )}
                </React.Fragment>
              );
            });
          })()}

          {/* Show message if no categories */}
          {categories.length === 0 && (
            <div className="mt-10 text-center text-gray-500" role="status" aria-live="polite">
              <div className="flex flex-col items-center gap-4">
                <div className="text-6xl opacity-20" aria-hidden="true">🏷️</div>
                <div>
                  <h3 className="text-lg font-medium mb-2">No categories available</h3>
                  <p className="text-sm">Categories will appear here when they become available</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Show ProductCarousel at the end if it wasn't shown in the middle (for odd number of categories or no categories) */}
        {(categories.length === 0 || categories.length <= 2) && (
          <div className="w-full my-6">
            <ProductCarousel />
          </div>
        )}
      </main>

      <PromotionalSection/>
    </div>
  );
}

export default Products;
